<template>
  <div
    ref="container"
    class="relative">
    <Container class="relative top-0 z-10 flex items-center py-12 md:py-20 lg:sticky lg:h-screen lg:py-24">
      <h2
        class="w-full whitespace-pre-line text-4xl text-white duration-1000 md:max-w-lg md:text-5xl lg:max-w-none lg:text-9xl"
        v-html="copy" />
    </Container>
    <Container class="relative z-20 overflow-hidden pb-12 md:pb-20 lg:overflow-visible lg:pb-[90vh]">
      <div
        v-if="isDesktop"
        class="-mx-8 grid grid-cols-3 gap-16">
        <CardGradientVertical
          v-for="(card, i) in cards"
          :key="i"
          :title="card.title"
          :copy="card.copy"
          :background-gradient="card.backgroundGradient"
          :background-image="card.backgroundImage"
          class="row-span-4"
          :class="[
            { 'col-start-2 row-start-1': i === 0 },
            { 'col-start-1 row-start-3': i === 1 },
            { 'col-start-3 row-start-4': i === 2 }
          ]" />
      </div>
      <Slider v-else>
        <CardGradientVertical
          v-for="(card, i) in cards"
          :key="i"
          :title="card.title"
          :copy="card.copy"
          :background-gradient="card.backgroundGradient"
          :background-image="card.backgroundImage"
          class="keen-slider__slide flex-shrink-0" />
      </Slider>
    </Container>
    <div class="pointer-events-none absolute top-0 h-full w-full">
      <div class="sticky top-0 h-screen overflow-hidden">
        <div
          v-if="gradientTop"
          class="absolute -left-36 -top-36 z-0">
          <div
            class="h-[100vw] w-[100vw] rounded-full blur-3xl duration-150 md:h-[50vw] md:w-[50vw] md:blur-2xl"
            :class="[`bg-${gradientTop}`]"
            :style="{
              transform: 'scale3d(1, 1, 1)',
              opacity: opacity
            }" />
        </div>
        <div
          v-if="gradientBottom"
          class="absolute left-0 right-0 z-0"
          :style="{
            transform: `translate3d(0, ${position}%, 0)`
          }">
          <div
            class="h-[200vw] w-[200vw] rounded-full blur-3xl duration-150 md:blur-2xl lg:h-[100vw] lg:w-[100vw]"
            :class="[`bg-${gradientBottom}`]"
            :style="{
              transform: `scale3d(${scale}, ${scale}, 1)`
            }" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, ref } from 'vue'

  import { scroll } from 'motion'

  import { useScreenSize } from '~/composables/use-screen-size'

  defineProps<ScrollingCardsProps>()

  const { isDesktop } = useScreenSize()

  type ScrollingCardsProps = {
    copy: string
    cards: {
      title: string
      copy?: string
      backgroundGradient?: string
      backgroundImage?: string
    }[]
    gradientTop?: string
    gradientBottom?: string
  }

  const container = ref<HTMLElement | null>(null)
  const position = ref(90)
  const scale = ref(1)
  const opacity = ref(1)

  onMounted(() => {
    if (typeof window === 'undefined') {
      return
    }
    if (!container.value) {
      return
    }

    scroll(
      ({ y }) => {
        const progress = y.progress
        opacity.value = Math.max(0, Math.min(1, 1 - (progress - 0.3) * 4))
        position.value = (1 - progress / 2) * 100
        scale.value = 1 + (progress - 0.4) * 2
      },
      {
        target: container.value,
        offset: ['start end', 'end end']
      }
    )
  })
</script>
